import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import { formatByType } from "../Reports";

export default function OrdersInPeriod({ data, hasReportFilters }) {
  const [selectedOption, setSelectedOption] = useState({
    name: "Faturamento",
    id: "FATURAMENTO",
  });

  const _data = {
    FATURAMENTO: {
      series: ["Faturamento líquido", "Taxa de frete", "Taxa de serviço"],
      value_type: "currency",
      colors: ["#f288a4", "#1d64f2", "#bff205"],
      data: [
        data.map((d) => d.service_total.value || 0),
        data.map((d) => d.delivery_total.value || 0),
        data.map((d) => d.total_without_delivery_and_service.value || 0),
      ],
    },
    TAXA_SERVICO: {
      series: ["Taxa de serviço"],
      value_type: "currency",
      colors: ["#f288a4"],
      data: [data.map((d) => d.service_total.value || 0)],
    },
    TAXA_FRETE: {
      series: ["Taxa de frete"],
      value_type: "currency",
      colors: ["#f288a4"],
      data: [data.map((d) => d.delivery_total.value || 0)],
    },
    QTD_PEDIDOS: {
      series: ["Qtd. Pedidos"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.order_quantity.value || 0)],
    },
    TICKET_MEDIO: {
      series: ["Ticket médio"],
      value_type: "currency",
      colors: ["#f288a4"],
      data: [data.map((d) => d.average_order_value.value || 0)],
    },
    PEDIDOS_P_CLIENTE: {
      series: ["Pedidos por cliente"],
      value_type: "float",
      colors: ["#f288a4"],
      data: [data.map((d) => d.orders_per_client.value || 0)],
    },
    PRODUTOS_P_CLIENTE: {
      series: ["Produtos por pedido"],
      value_type: "float",
      colors: ["#f288a4"],
      data: [data.map((d) => d.products_per_order.value || 0)],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("orders-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });
      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            const formatGrowth = (growth) => {
              return "";
            };

            if (selectedOption.id !== "FATURAMENTO") return "";

            return `
              <div>
                ${
                  selectedOption.id === "FATURAMENTO"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <div style="color:#fff;opacity:70%;line-height:16px;font-weight:600;margin-bottom:2px;">
                    ${item.date}
                  </div>

                  <span style="color:#8a8af5;font-weight:700">
                    Faturamento bruto:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.order_total.value)} 
                  </span>  
                  ${formatGrowth(item.order_total.growth)}
                </div>

                                <div style="background-color: rgba(29, 100, 242, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#578cf5;font-weight:700">
                    Taxa de frete:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.delivery_total.value)} 
                  </span>  
                  ${formatGrowth(item.delivery_total.growth)}
                </div>

                <div style="background-color: rgb(242 136 164 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#f288a4;font-weight:700">
                    Taxa de serviço:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.service_total.value)} 
                  </span>
                  ${formatGrowth(item.service_total.growth)}
                </div>
                
                                <div style="background-color: rgba(191 242 5 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;">
                  <span style="color:#bff205;font-weight:700">
                    Faturamento líquido:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(
                      +item.total_without_delivery_and_service.value
                    )} 
                  </span>  
                  ${formatGrowth(
                    item.total_without_delivery_and_service.growth
                  )}
                </div>
                `
                    : ""
                }
              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                [
                  "QTD_PEDIDOS",
                  "PEDIDOS_P_CLIENTE",
                  "PRODUTOS_P_CLIENTE",
                ].includes(selectedOption.id)
                  ? Numbers.formatInt(value)
                  : Currency.formatCurrency(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data[selectedOptionId].series.map((name, sid, series) => {
          return {
            name,
            type: "bar",
            stack: "total",
            barWidth: "80%",
            data: _data[selectedOptionId].data[sid].map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show:
                sid == series.length - 1 && selectedOptionId !== "FATURAMENTO",
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const index = params.dataIndex;
                const total = _data[selectedOptionId].data.reduce(
                  (acc, curr) => acc + curr[index],
                  0
                );
                if (total <= 0) return "";
                return formatByType(total, _data[selectedOptionId].value_type);
              },
            },
            itemStyle: {
              color: _data[selectedOptionId].colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption]);

  const allEmpty = _data[selectedOption.id].data.every((c) =>
    c.every((d) => d === 0 || !d)
  );

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Pedidos no período</p>
        </div>
        <ComboFilters
          options={[
            { name: "Faturamento", id: "FATURAMENTO" },
            { name: "Qtd. Pedidos", id: "QTD_PEDIDOS" },
            { name: "Ticket médio", id: "TICKET_MEDIO" },
            { name: "Pedidos por cliente", id: "PEDIDOS_P_CLIENTE" },
            { name: "Produtos por pedido", id: "PRODUTOS_P_CLIENTE" },
          ]}
          onSelect={(option) => setSelectedOption(option)}
          selectedOption={
            selectedOption || { name: "Faturamento", id: "FATURAMENTO" }
          }
        />
      </div>

      <div className={styles.chart}>
        {allEmpty ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}

        <div id="orders-chart" style={{ width: "100%", height: 350 }} />
      </div>
    </div>
  );
}
